import React, { useState, useRef, useEffect } from "react";
import { IoOptionsOutline } from "react-icons/io5";

interface FilterSection {
    key: string;
    title: string;
    options: string[];
    type?: "checkbox" | "radio" | "date";
    formatLabel?: (option: string) => React.ReactNode;
}



interface FilterDropdownProps {
    sections: FilterSection[];
    onChange?: (selected: Record<string, string[]>) => void;
    dropdownClassName?: string;
    sideClass?: string;
    initialSelected?: Record<string, string[]>; // <-- add this
    resetValue?: Record<string, string[]>; // new
}


const FilterDropdown: React.FC<FilterDropdownProps> = ({
    sections,
    onChange,
    dropdownClassName = "", // default empty
    sideClass = "", // default empty
    initialSelected,
    resetValue,
}) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<Record<string, string[]>>(initialSelected || {});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [dates, setDates] = useState<Record<string, { start?: string; end?: string }>>({});

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    const toggleOption = (
        sectionKey: string,
        option: string,
        type: "checkbox" | "radio" = "checkbox"
    ) => {
        setSelected((prev) => {
            let updated: string[];

            if (type === "radio") {
                updated = [option]; // single value only
            } else {
                const current = prev[sectionKey] || [];
                updated = current.includes(option)
                    ? current.filter((o) => o !== option)
                    : [...current, option];
            }

            const newState = { ...prev, [sectionKey]: updated };

            if (onChange) onChange(newState);

            return newState;
        });
    };


    const handleReset = () => {
        const resetState = resetValue || initialSelected || {};
        setSelected(resetState);
        setDates({});  // ← add this line
        if (onChange) onChange(resetState);
    };

    const updateDate = (key: string, field: "start" | "end", value: string) => {
        setDates(prev => {
            const updated = {
                ...prev,
                [key]: {
                    ...prev[key],
                    [field]: value
                }
            };

            if (onChange) {
                onChange({
                    ...selected,
                    [key]: updated[key] ? [JSON.stringify(updated[key])] : []
                });
            }

            return updated;
        });
    };



    return (
        <div className="filter-wrapper" style={{ position: "relative" }} ref={wrapperRef}>
            <button onClick={() => setOpen((prev) => !prev)}>
                <IoOptionsOutline className="customer-filter-icon" /> Filter
            </button>

            {open && (
                <div className={`filter-dropdown ${dropdownClassName}`}>
                    <div className={sideClass}>
                        {sections.map((section) => (
                            <div key={section.key} className="filter-section">
                                <h4>{section.title}</h4>

                                {section.type === "date" ? (
                                    <div className="filter-section">
                                        {/* ← Delete the <h4>{section.title}</h4> that was here */}

                                        <label className="filter-option filter-option-for-date">
                                            <p>From</p>
                                            <input
                                                type="date"
                                                value={dates[section.key]?.start || ""}
                                                onChange={(e) =>
                                                    updateDate(section.key, "start", e.target.value)
                                                }
                                            />
                                        </label>

                                        <label className="filter-option filter-option-for-date">
                                            <p>To</p>
                                            <input
                                                type="date"
                                                value={dates[section.key]?.end || ""}
                                                onChange={(e) =>
                                                    updateDate(section.key, "end", e.target.value)
                                                }
                                            />
                                        </label>
                                    </div>
                                ) : (
                                    section.options.map((option) => (
                                        <label key={option} className="filter-option">
                                            <input
                                                type={section.type === "radio" ? "radio" : "checkbox"}
                                                name={section.type === "radio" ? section.key : undefined}
                                                checked={
                                                    section.type === "radio"
                                                        ? (selected[section.key] || [])[0] === option
                                                        : (selected[section.key] || []).includes(option)
                                                }
                                                onChange={() =>
                                                    toggleOption(
                                                        section.key,
                                                        option,
                                                        section.type === "radio" ? "radio" : "checkbox"
                                                    )
                                                }
                                            />

                                            {section.formatLabel
                                                ? section.formatLabel(option)
                                                : option}
                                        </label>
                                    ))
                                )}


                            </div>
                        ))}
                    </div>

                    <div className="filter-actions">
                        <button onClick={handleReset}>Reset</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
